import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../../hooks/useStore';
import { IOvertimeParam } from '../../../../models/overtime/OvertimeModel';
import CommonButton from '../../../../components/common-components/button/CommonButton';
import styles from './NewOvertimePopup.module.css';
import { X } from 'lucide-react';

const NewLeavePopup = () => {
  const { popupStore, overtimeStore, authStore } = useStore();
  const [formData, setFormData] = useState<IOvertimeParam>({
    startDate: new Date(),
    endDate: new Date(),
    description: '',
    status: 'Pending',
    overtimeType: 'Leave',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Form açıldığında tarihleri bugünün tarihi ile başlat
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    setFormData(prev => ({
      ...prev,
      startDate: today,
      endDate: tomorrow,
    }));
  }, []);

  const calculateTotalHours = (start: Date, end: Date): number => {
    const diffInMs = end.getTime() - start.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);
    return Math.round(diffInHours * 100) / 100; // 2 decimal places
  };

  const formatDateTimeForInput = (date: Date): string => {
    // Geçersiz tarih kontrolü
    if (!date || isNaN(date.getTime())) {
      return '';
    }

    // Basit format - timezone sorununu önlemek için
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const parseDateTimeFromInput = (inputValue: string): Date => {
    if (!inputValue) {
      return new Date();
    }

    // Basit parse - timezone sorununu önlemek için
    const date = new Date(inputValue);

    // Geçersiz tarih kontrolü
    if (isNaN(date.getTime())) {
      return new Date();
    }

    return date;
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.startDate) {
      newErrors.startDate = 'Başlangıç tarihi gereklidir';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'Bitiş tarihi gereklidir';
    }

    if (formData.startDate && formData.endDate && formData.startDate >= formData.endDate) {
      newErrors.endDate = 'Bitiş tarihi başlangıç tarihinden sonra olmalıdır';
    }

    if (!formData.description || formData.description.trim() === '') {
      newErrors.description = 'Açıklama gereklidir';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const totalHours = calculateTotalHours(formData.startDate, formData.endDate);

      const leaveData: IOvertimeParam = {
        ...formData,
        userId: authStore.user?.id?.toString(), // Convert to string
        totalHours: totalHours,
      };

      await overtimeStore.createOvertime(leaveData);
      popupStore.setIsNewLeaveOpen(false);

      // Form'u temizle
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      setFormData({
        startDate: today,
        endDate: tomorrow,
        description: '',
        status: 'Pending',
        overtimeType: 'Leave',
      });
      setErrors({});
    } catch (error) {
      console.error('Error creating leave:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    popupStore.setIsNewLeaveOpen(false);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    setFormData({
      startDate: today,
      endDate: tomorrow,
      description: '',
      status: 'Pending',
      overtimeType: 'Leave',
    });
    setErrors({});
  };

  const handleInputChange = (field: keyof IOvertimeParam, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Hata varsa temizle
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  return (
    <div className={styles.popupOverlay} onClick={handleClose}>
      <div className={styles.popupContent} onClick={e => e.stopPropagation()}>
        <div className={styles.popupHeader}>
          <h3 className={styles.popupTitle}>Yeni İzin Kaydı</h3>
          <button className={styles.closeButton} onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Başlangıç Tarihi</label>
              <input
                type="datetime-local"
                className={styles.formInput}
                value={formData.startDate ? formatDateTimeForInput(formData.startDate) : ''}
                onChange={e =>
                  handleInputChange('startDate', parseDateTimeFromInput(e.target.value))
                }
              />
              {errors.startDate && <div className={styles.errorMessage}>{errors.startDate}</div>}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Bitiş Tarihi</label>
              <input
                type="datetime-local"
                className={styles.formInput}
                value={formData.endDate ? formatDateTimeForInput(formData.endDate) : ''}
                onChange={e => handleInputChange('endDate', parseDateTimeFromInput(e.target.value))}
              />
              {errors.endDate && <div className={styles.errorMessage}>{errors.endDate}</div>}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Toplam Saat</label>
            <input
              type="text"
              className={styles.formInput}
              value={
                formData.startDate && formData.endDate
                  ? calculateTotalHours(formData.startDate, formData.endDate) + ' saat'
                  : '0 saat'
              }
              readOnly
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Açıklama</label>
            <textarea
              className={styles.formTextarea}
              value={formData.description}
              onChange={e => handleInputChange('description', e.target.value)}
              placeholder="İzin açıklamasını giriniz..."
            />
            {errors.description && <div className={styles.errorMessage}>{errors.description}</div>}
          </div>

          <div className={styles.buttonGroup}>
            <CommonButton type="secondary" onClick={handleClose}>
              İptal
            </CommonButton>
            <CommonButton
              type="primary"
              onClick={() => handleSubmit(new Event('submit') as any)}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
            </CommonButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default observer(NewLeavePopup);
